import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../../../constants";
import AppService from "../../../../services/app.service";
import {
  getMultiSelectOptions,
  handleMultiSelectChange,
} from "../../../../utils/utils";
import {
  ALL_FILTER_KEYS,
  buildCriteria,
  emptySelectedFilters,
  FILTER_META,
  PAGE_SIZE,
  SELECT_ALL_OPTIONS,
} from "./simulationLibrary.constants";

/**
 * Shared hook for paginated simulation library pages.
 * @param {Object} options
 * @param {string[]} [options.filterKeys] - Which sidebar filters to show/load
 * @param {boolean} [options.useAppService=false] - POST to view/paginated?page=&size= via AppService (auth)
 * @param {number} [options.account=0] - Account id for scoped results
 */
export const useSimulationLibrary = ({
  filterKeys = ALL_FILTER_KEYS,
  useAppService = false,
  account = 0,
} = {}) => {
  const [sims, setSims] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(() => emptySelectedFilters(filterKeys));
  const [selectedFilters, setSelectedFilters] = useState(() =>
    emptySelectedFilters(filterKeys),
  );
  const [isShowLastTwelveMonths, setIsShowLastTwelveMonths] = useState(false);

  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);
  const appliedCriteriaRef = useRef(null);

  const makeCriteria = (
    appliedFilters = selectedFilters,
    showLastTwelveMonths = isShowLastTwelveMonths,
    searchTerm = search,
  ) =>
    buildCriteria({
      filterKeys,
      selectedFilters: appliedFilters,
      searchTerm,
      showLastTwelveMonths,
      account,
    });

  const fetchPage = async (pageToLoad, criteria, { preempt = false } = {}) => {
    if (!preempt && loadingRef.current) return;
    loadingRef.current = true;

    const requestId = ++requestIdRef.current;
    const isFirstPage = pageToLoad === 0;
    if (isFirstPage) {
      setLoading(true);
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = useAppService
        ? await AppService.submit(
            criteria,
            `sims/view/paginated?page=${pageToLoad}&size=${PAGE_SIZE}`,
          )
        : await axios.post(
            `${API_ENDPOINT}sims/public/filtered/paginated?page=${pageToLoad}&size=${PAGE_SIZE}`,
            criteria,
          );

      if (requestId !== requestIdRef.current) return;

      const data = response.data;
      const content = Array.isArray(data?.content) ? data.content : [];
      setSims((prev) => {
        if (isFirstPage) return content;
        const seen = new Set(prev.map((sim) => sim.simId));
        return [...prev, ...content.filter((sim) => !seen.has(sim.simId))];
      });
      setHasMore(!data?.last);
      setPage(pageToLoad + 1);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error fetching simulations:", err);
      setHasMore(false);
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        if (isFirstPage) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }
  };

  const applyCriteria = (criteria) => {
    appliedCriteriaRef.current = criteria;
    setHasMore(true);
    fetchPage(0, criteria, { preempt: true });
  };

  const createMultiSelectHandler = (filterKey) => (value, action) => {
    let list = handleMultiSelectChange(
      value,
      action,
      filters[filterKey],
      selectedFilters[filterKey],
      SELECT_ALL_OPTIONS[filterKey],
    );
    list = list.filter((item) => item.value !== "*");
    setSelectedFilters((prev) => ({ ...prev, [filterKey]: list }));
  };

  const getFilterOptions = (filterKey) =>
    getMultiSelectOptions(
      filters[filterKey],
      selectedFilters[filterKey],
      SELECT_ALL_OPTIONS[filterKey],
    );

  const filterOptions = filterKeys.reduce((acc, key) => {
    const meta = FILTER_META[key];
    acc[key] = {
      label: meta.label,
      placeholder: meta.placeholder,
      onChange: createMultiSelectHandler(key),
      options: getFilterOptions(key),
    };
    return acc;
  }, {});

  const handleSearchInput = (event) => {
    const value = event.target.value;
    setSearch(value);
    if (value.trim() === "" && appliedCriteriaRef.current?.search) {
      applyCriteria({ ...appliedCriteriaRef.current, search: "" });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyCriteria(makeCriteria());
  };

  const handleFilterClick = () => {
    applyCriteria(makeCriteria());
  };

  const handleChangeLastMonth = (event) => {
    const checked = event.target.checked;
    setIsShowLastTwelveMonths(checked);
    applyCriteria(
      makeCriteria(
        selectedFilters,
        checked,
        appliedCriteriaRef.current?.search ?? "",
      ),
    );
  };

  useEffect(() => {
    let active = true;
    applyCriteria(makeCriteria());

    const loadFilterOptions = async () => {
      const results = await Promise.allSettled(
        filterKeys.map((key) => {
          const endpoint = FILTER_META[key].endpoint;
          return useAppService
            ? AppService.getAll(endpoint)
            : axios.get(`${API_ENDPOINT}${endpoint}`);
        }),
      );
      if (!active) return;

      const loaded = {};
      results.forEach((result, index) => {
        const key = filterKeys[index];
        if (result.status === "rejected" || !Array.isArray(result.value?.data)) {
          console.error(
            `Error loading filter options (${FILTER_META[key].endpoint}):`,
            result.status === "rejected" ? result.reason : result.value?.data,
          );
          return;
        }
        const data = result.value.data;
        loaded[key] = FILTER_META[key].transform
          ? FILTER_META[key].transform(data)
          : data;
      });
      setFilters((prev) => ({ ...prev, ...loaded }));
    };

    loadFilterOptions();

    return () => {
      active = false;
      requestIdRef.current++;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 80 >=
          document.documentElement.scrollHeight &&
        hasMore &&
        !loadingRef.current
      ) {
        fetchPage(page, appliedCriteriaRef.current ?? makeCriteria());
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, hasMore]);

  useEffect(() => {
    if (!hasMore || loadingRef.current) return;
    if (document.documentElement.scrollHeight <= window.innerHeight + 80) {
      fetchPage(page, appliedCriteriaRef.current ?? makeCriteria());
    }
  }, [sims, hasMore, page]);

  return {
    sims,
    loading,
    loadingMore,
    search,
    selectedFilters,
    isShowLastTwelveMonths,
    filterOptions,
    handleSearchInput,
    handleSearch,
    handleFilterClick,
    handleChangeLastMonth,
  };
};

export default useSimulationLibrary;
